import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { correiosApi } from "@/services";
import { useTenant } from "./useTenant";
import { useAllIntegrationsStatus } from "./useIntegrationsStatus";

interface CorreiosStatus {
  connected: boolean;
  cartao_postagem?: string;
  ambiente?: string;
}

interface CorreiosShippingService {
  id: string;
  name: string;
  price: string;
  currency: string;
  delivery_time: number;
  delivery_range: {
    min: number;
    max: number;
  };
  company: {
    id: number;
    name: string;
    picture: string;
  };
  provider: string;
}

interface ConnectParams {
  usuario: string;
  chave: string;
  cartao_postagem: string;
}

interface CalculateParams {
  cepOrigem: string;
  cepDestino: string;
  peso?: number;
  comprimento?: number;
  largura?: number;
  altura?: number;
}

export function useCorreiosStatus() {
  const { data: integrations, isLoading } = useAllIntegrationsStatus();

  const data = useMemo((): CorreiosStatus => {
    const integration = integrations?.find(
      (i) => i.provider === "correios" && i.is_active
    );
    if (!integration) return { connected: false };
    const metadata = integration.metadata as Record<string, unknown> | null;
    return {
      connected: true,
      cartao_postagem: (metadata?.cartao_postagem as string) || undefined,
      ambiente: (metadata?.ambiente as string) || undefined,
    };
  }, [integrations]);

  return { data, isLoading };
}

export function useConnectCorreios() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: ConnectParams) => {
      return await correiosApi.connect(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useDisconnectCorreios() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async () => {
      await correiosApi.disconnect();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations-status", tenant?.id] });
    },
  });
}

export function useCorreiosCalculateShipping() {
  return useMutation({
    mutationFn: async (params: CalculateParams): Promise<CorreiosShippingService[]> => {
      const data = await correiosApi.calculateShipping(params);
      return data as CorreiosShippingService[];
    },
  });
}

export function useCorreiosCreateShipment() {
  const queryClient = useQueryClient();
  const { tenant } = useTenant();

  return useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      return await correiosApi.createShipment(params);
    },
    onSuccess: (_, variables) => {
      const saleId = variables.sale_id as string;
      queryClient.invalidateQueries({ queryKey: ["shipments", saleId, tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["sales", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-metrics", tenant?.id] });
    },
  });
}
